import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux"
import debounce from 'lodash.debounce';
import { Alert, AlertDescription, AlertTitle, Button, CloseButton, Divider, Flex, Input, InputGroup, InputLeftElement, InputRightElement, SimpleGrid, Stack, Text, useDisclosure } from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import Modal from "components/modal/Modal";
import { FaArrowDown, FaArrowUp, FaPlusCircle } from "react-icons/fa";
import TooltipComponent from "components/tooltip";
import { FiSearch } from "react-icons/fi";
import ButtonPrimary from "components/button/ButtonPrimary";
import { IoAddCircle, IoCalendarOutline, IoCartOutline, IoPersonOutline, IoWalletOutline } from "react-icons/io5";
import { SET_TO_DEFAULT_RESPONSE, create, reabrircaja, del, getByPage, getCurrentCaja, cerrarCaja } from "./../../../store/features/slices/cajaSlice";
import { message as alerta } from "utils/Funciones";
import { uriSerialized } from "utils/uriSerialized";
import { alertaConfirmar } from "utils/Funciones";
import SwitchToggle from "components/fields/SwitchToggle";
import Widget from "components/widget/Widget";
import { FaCashRegister, FaCoins, FaDoorClosed, FaHashtag, FaUserShield } from "react-icons/fa6";
import Spinner from "components/spinner/Spinner";
import { currencyFormat } from "utils/Funciones";
import moment from "moment"
import 'moment/locale/es';
import TablePagination from "./table/TablePagination";

const Caja = () => {
    const dispatch = useDispatch();
    const { userToken, user } = useSelector((state) => state.userReducer);
    const {
        dataByPage,
        totalPage,
        totalRow,
        currentCaja,
        responseSuccessCreate,
        isLoading,
        isSuccess,
        isError,
        message,
        responseSuccessDelete,
        responseSuccessUpdate
    } = useSelector((state) => state.cajaReducer);

    const registrar = useDisclosure()
    const reabrir = useDisclosure()
    const [searchTerm, setSearchTerm] = useState('');
    const [state, setState] = useState({
        monto_inicial: '',
        disabled: false
    })

    const [stateReabrirCaja, setStateReabrirCaja] = useState({
        _id: '',
        admin_email: '',
        admin_password: ''
    })

    const setDefaultState = () => {
        setState({
            ...state,
            monto_inicial: '',
            disabled: false
        })
    }

    function handleReabrirCaja(row) {
        setStateReabrirCaja({
            ...stateReabrirCaja,
            _id: row._id
        })
        reabrir.onOpen()
    }

    function handleDelete(row) {
        alertaConfirmar('Eliminar Caja', `Estas seguro que quieres eliminar la caja <b>${row.name}</b>? recuerda que se eliminarán todos los datos relacionados a este.`, 'question', async (res_alert) => {
            if (res_alert === true) {
                await dispatch(del({ _id: row._id, userId: userToken && user.id }));
            }
        })
    }

    const handleInputs = e => {
        setState({ ...state, [e.target.name]: e.target.value })
    }

    const handleReabrirCajaInputs = e => {
        setStateReabrirCaja({ ...stateReabrirCaja, [e.target.name]: e.target.value })
    }

    const onSubmitRegistrar = async e => {
        e.preventDefault();
        if (state.monto_inicial >= 0) {
            const formData = new FormData();
            state.userId = userToken && user.id
            formData.append('data', JSON.stringify(state));
            await dispatch(create({ formData: formData, token: userToken }));

        } else {
            alerta('Monto inicial debe ser mayor o igual a cero', 'error', 4)
        }
    }

    const onSubmitReabrirCaja = async e => {
        e.preventDefault();
        if (stateReabrirCaja.admin_email.trim().length > 0 && stateReabrirCaja.admin_password.trim().length > 0) {
            const formData = new FormData();
            formData.append('data', JSON.stringify(stateReabrirCaja));
            await dispatch(reabrircaja(formData));

        } else {
            alerta('credenciales de ADMIN son requeridos', 'error', 4)
        }
    }

    const handleCloseCaja = () => {
        dispatch(cerrarCaja());
    }

    useEffect(() => {
        dispatch(getCurrentCaja({ token: userToken }));
    }, [dispatch, responseSuccessCreate])

    useEffect(() => {
        if (responseSuccessCreate) {
            alerta(responseSuccessCreate.message, 'success', 5)
            setState({ ...state, name: "", direccion: "" })
            registrar.onClose()
        }
        dispatch(SET_TO_DEFAULT_RESPONSE())
    }, [responseSuccessCreate])

    useEffect(() => {
        if (responseSuccessDelete) {
            alerta(responseSuccessDelete.message, 'success', 5)
        }
        dispatch(SET_TO_DEFAULT_RESPONSE())
    }, [responseSuccessDelete])

    useEffect(() => {
        if (responseSuccessUpdate) {
            alerta(responseSuccessUpdate.message, 'success', 5)
            setStateReabrirCaja({ ...stateReabrirCaja, admin_email: "", admin_password: "" })
            dispatch(getCurrentCaja({ token: userToken }));
            reabrir.onClose()
        }
        dispatch(SET_TO_DEFAULT_RESPONSE())
    }, [responseSuccessUpdate])

    const fetchData = useCallback(
        (pageSize, pageIndex, search, order) => {
            const queryOptions = {
                page: pageIndex,
                limit: pageSize,
                search: search,
                order: order,
            }
            const query = queryOptions ? "?" + uriSerialized(queryOptions) : ""
            dispatch(getByPage({ query: query, token: userToken }))
        },
        [responseSuccessDelete, responseSuccessCreate, responseSuccessUpdate]
    )

    const onChangeSearchTerm = e => {
        setSearchTerm(e.target.value);
    };

    const onChangeSearchTermClear = e => {
        setSearchTerm("");
        document.getElementById("search").value = "";
    };

    const debounceSearchTerm = useMemo(() => debounce(onChangeSearchTerm, 700), []);

    return (
        <div className="mt-4 md:mt-0">
            <div className="sticky z-30 w-full top-44 md:top-28 flex items-center justify-end bg-white dark:!bg-navy-800 p-2 rounded-xl shadow-xl shadow-gray-200 dark:shadow-none">
                <div className="relative flex h-[61px] w-[calc(100%-55px)] flex-grow items-center gap-2 rounded-full bg-white px-0 py-2 dark:!bg-navy-800">
                    <InputGroup className="!w-full md:!w-[300px]">
                        <InputLeftElement pointerEvents="none" className="!mt-1 !ml-1">
                            <FiSearch size={24} className="text-navy-500 dark:text-navy-300" />
                        </InputLeftElement>
                        <Input size='lg' autoFocus id="search" onChange={debounceSearchTerm} type="text" placeholder={`Buscar ${totalRow} cajas...`} className="flex w-full items-center justify-center !rounded-xl border !bg-lightPrimary 
            dark:!bg-navy-900 !text-sm !ring-none focus:!ring-brand-500 
            focus:!border-brand-500 dark:focus:!ring-brand-400 dark:focus:!border-brand-400 
            !outline-none !border-gray-200 dark:!border-white/10 dark:text-white" />
                        <InputRightElement className="!mt-1">
                            <CloseButton onClick={onChangeSearchTermClear} size='md' className={`mr-2 ${searchTerm.trim().length > 0 ? 'scale-100' : 'scale-0'} transition-all duration-500 text-navy-500 dark:text-navy-300`} />
                        </InputRightElement>
                    </InputGroup>
                </div>
                <div className="w-[55px] h-[61px] flex items-center">
                    <TooltipComponent
                        trigger={<button onClick={registrar.onOpen} className="bg-brand-500 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:bg-brand-200 flex items-center justify-center rounded-full p-3 text-3xl text-white transition duration-200 hover:cursor-pointer dark:text-white">
                            <FaPlusCircle />
                        </button>}
                        content={
                            <p className="dark:text-white">
                                Aperturar caja
                            </p>
                        }
                    />
                </div>
            </div>
            <div className="mt-3 md:mt-8">
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={5}>
                    <Widget
                        icon={<FaHashtag className="h-7 w-7 text-purple-500 dark:text-purple-300" />}
                        title={"Nombre de caja"}
                        subtitle={isLoading ? <Spinner /> : currentCaja.name}
                    />
                    <Widget
                        icon={<IoPersonOutline className="h-7 w-7 text-blue-500 dark:text-blue-300" />}
                        title={"Nombre del cajero"}
                        subtitle={isLoading ? <Spinner /> : currentCaja.cajero}
                    />
                    <Widget
                        icon={<IoCalendarOutline className="h-7 w-7 text-teal-500 dark:text-teal-300" />}
                        title={"Fecha de apertura"}
                        subtitle={
                            isLoading ? (
                                <Spinner />
                            ) : currentCaja.fecha_apertura === "---" ? (
                                "---"
                            ) : (
                                moment(currentCaja.fecha_apertura).format("dddd, D [de] MMMM [de] YYYY, h:mm a")
                            )
                        }
                    />
                </SimpleGrid>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={4} mt={5}>
                    <Widget
                        icon={<IoCartOutline className="h-7 w-7 text-blue-500 dark:text-blue-300" />}
                        title={"Ventas"}
                        subtitle={isLoading ? <Spinner /> : currencyFormat(currentCaja.totalVentas)}
                    />
                    <Widget
                        icon={<FaArrowDown className="h-7 w-7 text-green-500 dark:text-green-300" />}
                        title={"Ingresos"}
                        subtitle={isLoading ? <Spinner /> : currencyFormat(currentCaja.totalIngresos)}
                    />
                    <Widget
                        icon={<FaArrowUp className="h-7 w-7 text-red-500 dark:text-red-300" />}
                        title={"Egresos"}
                        subtitle={isLoading ? <Spinner /> : currencyFormat(currentCaja.totalEgresos)}
                    />
                    <Widget
                        icon={<FaCoins className="h-7 w-7 text-yellow-500 dark:text-yellow-300" />}
                        title={"Saldo inicial"}
                        subtitle={isLoading ? <Spinner /> : currencyFormat(currentCaja.saldoInicial)}
                    />
                    <Widget
                        icon={<IoWalletOutline className="h-7 w-7 text-purple-500 dark:text-purple-300" />}
                        title={"Total en caja"}
                        subtitle={
                            isLoading ? <Spinner /> :
                                currencyFormat((currentCaja.totalVentas + currentCaja.totalIngresos + currentCaja.saldoInicial) - currentCaja.totalEgresos)
                        }
                    />
                    <Stack direction='row' spacing={4} align='center' justify='center'>
                        <Button
                            onClick={handleCloseCaja}
                            type="button"
                            isLoading={isLoading}
                            isDisabled={currentCaja.isClosed}
                            leftIcon={<FaDoorClosed size={45} />}
                            loadingText='Cerrando'
                            colorScheme='red'
                            variant='solid'
                            spinnerPlacement='start'
                            size='lg'
                            className="!p-10 !w-full !h-[90px]"
                        >
                            {currentCaja.isClosed ? 'Caja cerrada' : 'Cerrar caja'}
                        </Button>
                    </Stack>
                </SimpleGrid>


                {/** REGISTRAR */}
                <Modal isOpen={registrar.isOpen} onClose={registrar.onClose} headerTitle='Aperturar caja'>
                    <div className="px-[5px] md:px-[10px] pt-[20px] pb-[20px] max-w-[1368px] flex flex-col !z-[1004]">
                        <form className="w-full" onSubmit={onSubmitRegistrar}>
                            <div className="flex flex-wrap">
                                <InputField
                                    autoFocus={true}
                                    required={true}
                                    variant="input"
                                    extra="w-full md:w-6/12 mb-3 p-2"
                                    label="Monto inicial"
                                    placeholder="Monto inicial"
                                    id="monto_inicial"
                                    min={0}
                                    type="number"
                                    name="monto_inicial"
                                    onChange={handleInputs}
                                    value={state.monto_inicial}
                                />

                                <SwitchToggle
                                    extra="w-full mb-3 p-2"
                                    label="Estado"
                                    id="disabled"
                                    onClick={() => setState({
                                        ...state,
                                        disabled: !state.disabled
                                    })}
                                    value={state.disabled}
                                />

                                <ButtonPrimary isLoading={isLoading} disabled={false} icon={<IoAddCircle size={28} />}>
                                    Aperturar
                                </ButtonPrimary>
                            </div>
                        </form>
                    </div>
                </Modal>

                {/** EDITAR */}
                <Modal isOpen={reabrir.isOpen} onClose={reabrir.onClose} headerTitle='Reaperturar caja'>
                    <div className="px-[5px] md:px-[10px] pt-[20px] pb-[20px] max-w-[1368px] flex flex-col !z-[1004]">
                        <Alert
                            status="error"
                            variant="subtle"
                            bg="red.50"
                            color="red.700"
                            dark={{
                                bg: "red.900",
                                color: "red.200"
                            }}
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            textAlign="center"
                            rounded="xl"
                            shadow="lg"
                            p={6}
                            h="auto"
                            minH="180px"
                        >
                            <Flex
                                align="center"
                                justify="center"
                                bg="red.100"
                                _dark={{ bg: "red.700" }}
                                rounded="full"
                                w="14"
                                h="14"
                                mb={3}
                            >
                                <FaUserShield size={36} className="text-red-500 dark:text-red-500" />
                            </Flex>

                            <AlertTitle fontSize="xl" fontWeight="bold" mb={1}>
                                Se requieren credenciales de ADMINISTRADOR
                            </AlertTitle>

                            <AlertDescription maxW="sm" fontWeight="semibold">
                                Solo un <Text as="span" color="red.500" darkColor="red.300">ADMINISTRADOR</Text> puede reaperturar la caja.
                            </AlertDescription>
                        </Alert>

                        <form className="w-full mt-2" onSubmit={onSubmitReabrirCaja}>
                            <div className="flex flex-wrap">
                                <InputField
                                    autoFocus={true}
                                    autoComplete="off"
                                    required={true}
                                    variant="input"
                                    extra="w-full md:w-6/12 mb-3 p-2"
                                    label="Email"
                                    placeholder="Email"
                                    id="admin_email"
                                    type="text"
                                    name="admin_email"
                                    onChange={handleReabrirCajaInputs}
                                    value={stateReabrirCaja.admin_email}
                                />

                                <InputField
                                    required={true}
                                    autoComplete="off"
                                    variant="input"
                                    extra="w-full md:w-6/12 mb-3 p-2"
                                    label="Contraseña"
                                    placeholder="Contraseña"
                                    id="admin_password"
                                    type="password"
                                    name="admin_password"
                                    onChange={handleReabrirCajaInputs}
                                    value={stateReabrirCaja.admin_password}
                                />

                                <ButtonPrimary isLoading={isLoading} disabled={false} icon={<FaCashRegister size={28} />}>
                                    Reaperturar caja
                                </ButtonPrimary>
                            </div>
                        </form>
                    </div>
                </Modal>

                <div className="mt-5 grid h-full grid-cols-1">

                    <TablePagination
                        searchTerm={searchTerm}
                        tableData={dataByPage}
                        fetchData={fetchData}
                        loading={isLoading}
                        pageCount={totalPage}
                        totalRow={totalRow}
                        actions={{
                            onReopenBox: (item) => handleReabrirCaja(item),
                            onDelete: (item) => handleDelete(item)
                        }}
                    />
                </div>
            </div>
        </div >

    );
};

export default Caja;
